// LoginPage.tsx
import { LoginForm } from "@/components/Login/LoginForm";
import { SuccessDialog } from "@/components/Login/SuccessDialog";
import { ContainerScroll } from "@/components/ui/container-scroll-animation";
import { SparklesCore } from "@/components/ui/sparkles";
import {
  json,
  redirect,
  type ActionFunctionArgs,
  type LoaderFunctionArgs,
  type MetaFunction,
} from "@remix-run/node";
import {
  Link,
  useActionData,
  useLoaderData,
  useNavigation,
  useSearchParams
} from "@remix-run/react";
import logo from "app/assets/images/AmandaLogo.png";
import salesfunnel from "app/assets/images/SalesFunnel.png";
import movinglogo from "app/assets/images/svg/movinglogo.svg";
import { Loader } from "lucide-react";
import { z } from "zod";
import { logFailedSignIn, logSignIn, verifyLogin } from "~/models/user.server";
import { createUserSession, getUserId } from "~/session.server";
import { safeRedirect } from "~/utils";

const LoginSchema = z.object({
  email: z.string({ required_error: "Email is required" }).email("Email is invalid"),
  password: z.string({ required_error: "Password is required" }).min(8, "Password is too short"),
  redirectTo: z.string().default("/"),
  remember: z.string().optional(),
});

export const loader = async ({ request, context }: LoaderFunctionArgs) => {
  const clientid = process.env.CLIENT_ID;
  if (!clientid) {
    throw new Error("Invalid client");
  }

  const userId = await getUserId(request);
  if (userId) return redirect("/");
  return json({ message: "ok", clientid, nonce: context.nonce });
};

export const action = async ({ request }: ActionFunctionArgs) => {
  const formData = Object.fromEntries(await request.formData());
  const result = LoginSchema.safeParse(formData);

  if (!result.success) {
    return json({ errors: result.error.flatten().fieldErrors }, { status: 400 });
  }

  const { email, password, redirectTo, remember } = result.data;
  const user = await verifyLogin(email, password);

  if (!user) {
    await logFailedSignIn({ request, username: email });
    return json({ errors: { email: "Invalid email or password" } }, { status: 400 });
  }

  await logSignIn({ request, userId: user.id });
  return createUserSession({
    redirectTo: safeRedirect(redirectTo, "/"),
    remember: remember === "on",
    request,
    userId: user.id,
  });
};

export const meta: MetaFunction = () => [{ title: "Login" }];

export default function LoginPage() {
  const { clientid } = useLoaderData<typeof loader>();
  const [searchParams] = useSearchParams();
  const isSuccess = searchParams.get("success");
  const navigation = useNavigation();
  const redirectTo = searchParams.get("redirectTo") || "/";
  const actionData = useActionData<typeof action>();

  return (
    <div className="bg-black flex flex-col grow h-full">
      <div className="bg-black h-16 z-50 grow-0">
        <h1>
          <img
            src={logo}
            className="w-36 self-center py-6 translate-x-5"
            alt="Amanda AI logotype white capital letters"
          />
        </h1>
      </div>
      {navigation.state !== "idle" && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <Loader size={64} className="text-hausorange animate-spin" />
        </div>
      )}
      <div className="relative w-full grid self-center xl:grid-cols-2 grid-cols-1 max-w-screen-2xl items-center justify-center bg-black grow-0">
        <div className="absolute inset-0 z-10 w-full overflow-hidden">
          <SparklesCore
            fullscreen
            id="tsparticlesfullpage"
            background="transparent"
            minSize={0.4}
            maxSize={2.0}
            particleDensity={70}
            className="h-screen w-full z-10"
            particleColor="#FFFFFF"
            speed={1}
          />
        </div>
        <div className="transition-all w-full transform-gpu translate-x-0 xl:translate-x-24 2xl:translate-x-20 z-40 p-5 md:p-0 lg:m-0 flex break-keep text-pretty flex-col justify-center overflow-visible">
          <div className="mb-0 xl:w-[700px] overflow-visible">
            <p className="mb-7 text-sm font-semibold uppercase tracking-widest text-white">
              Login
            </p>
            <h1 className="hover:scale-105 transition-transform duration-700 ease-in-out tracking-tight text-wrap w-wull text-[4.6rem] leading-[3.7rem] mb-5 sm:text-[6rem] text-hausorange xl:mb-2 sm:leading-[4.2rem] md:text-[7rem] md:leading-[6.7rem] md:tracking-tight">
              Crunch those numbers!
            </h1>
          </div>
          <LoginForm
            clientid={clientid}
            redirectTo={redirectTo}
            actionData={actionData}
            isLoading={navigation.state !== "idle"}
          />
        </div>
        <div className="z-10 relative hidden sm:flex">
          <ContainerScroll>
            <img
              className="relative z-50 -mt-24 rotate-90 xl:rotate-0 transition-all delay-100 transform-gpu hover:scale-105  duration-700 "
              src={salesfunnel}
              alt="Image of two hands holding a funnel"
            />
          </ContainerScroll>
        </div>
        <SuccessDialog open={!!isSuccess} />
      </div>
      <div className="bg-hausorange px-8 pt-20 pb-48 z-30 grow">
        <div className="grid mx-auto max-w-7xl">
          <div className="py-8 border-t border-b border-black">
            <img src={movinglogo} alt="Amanda AI logo" className="w-24" />
          </div>
          <div className="border-b border-black py-7 grid gap-3">
            <h4 className="hauslabel text-xs">Our philosophy:</h4>
            <p className="text-5xl font-medium w-80">
              Dance to the algorithms!
            </p>
          </div>
          <div className="text-right">
            <Link to="https://amandaai.com/privacy-policy/" target="_blank" className="text-black font-light">
              Privacy policy
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
